import DateTimePicker from 'react-datetime-picker';
import { Formik, FormikHelpers } from 'formik';
import { Checkbox } from '@material-ui/core';
import toast from 'react-hot-toast';
import React from 'react';

import { finesService } from '@project/services';
import { Button } from '../../../atoms';
import { FineImageSchema } from './schema';
import { ReportFilterProps } from '../../../../types';
import { useDownloadFile } from '../../../../hooks/global';

export const FineImageReportForm: React.FC = () => {
  const initialValues: ReportFilterProps = {
    fromDate: null,
    toDate: null,
    viewAll: false,
    filterDateBy: 'CreatedDate',
  };

  const { download, loading } = useDownloadFile({
    apiDefinition: async (params: ReportFilterProps) =>
      finesService.getFineImageTotalsReport(params),
    onSuccess: () => toast.success('File downloaded successfully!'),
    onError: () => toast.error('Error downloading the file.'),
  });

  const handleSubmission = async (
    values: ReportFilterProps,
    { setSubmitting }: FormikHelpers<ReportFilterProps>,
  ) => {
    try {
      await download(values);
    } catch (error) {
      toast.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validateOnBlur
      validateOnChange={false}
      onSubmit={handleSubmission}
      validationSchema={FineImageSchema}
    >
      {({
        handleSubmit,
        isSubmitting,
        values,
        handleChange,
        handleBlur,
        handleReset,
        setFieldValue,
        errors,
        touched,
      }) => {
        const _setFromDate = (e) => {
          setFieldValue('fromDate', e);
        };
        const _setToDate = (e) => {
          setFieldValue('toDate', e);
        };

        return (
          <form
            onReset={handleReset}
            onSubmit={handleSubmit}
            className="flex bg-gray-100 shadow-lg rounded-sm"
          >
            <div className="flex flex-col justify-start items-start p-4 gap-4 w-1/2">
              <span className="w-full text-xl mb-2">Download Fine Images Report</span>
              <label htmlFor="filter-date-from" className="w-4/5 flex flex-col z-20">
                Filter From
                <DateTimePicker
                  id="filter-date-from"
                  clearIcon={null}
                  format="y-MM-dd"
                  onChange={_setFromDate}
                  value={values.fromDate}
                  disableClock
                />
                {touched.fromDate && errors.fromDate && (
                  <span className="text-red-500 text-sm mt-1">{errors.fromDate}</span>
                )}
              </label>
              <label htmlFor="filter-date-to" className="w-4/5 flex flex-col z-10">
                Filter To
                <DateTimePicker
                  id="filter-date-to"
                  clearIcon={null}
                  format="y-MM-dd"
                  onChange={_setToDate}
                  value={values.toDate}
                  disableClock
                />
                {touched.toDate && errors.toDate && (
                  <span className="text-red-500 text-sm mt-1">{errors.toDate}</span>
                )}
              </label>
              <div className="flex flex-row">
                <label htmlFor="viewAll" className="flex flex-col">
                  View All
                  <Checkbox
                    checked={values.viewAll}
                    name="viewAll"
                    id="viewAll"
                    color="secondary"
                    onChange={handleChange('viewAll')}
                    onBlur={handleBlur('viewAll')}
                    disableRipple
                  />
                </label>
              </div>
              <Button
                isLoading={isSubmitting || loading}
                onClick={handleSubmit}
                className="bg-tertiary hover:bg-tertiary-dark text-white text-base w-auto"
              >
                Download
              </Button>
            </div>
            <div className="w-1/2 bg-gray-200 rounded-sm m-5 p-2">
              <h1>Description</h1>
              <p className="pl-3 text-sm">The report returns the total fine images per provider.</p>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};
